import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UPDropdown, UPDropdownItem } from 'components/dropdown';
import {
  GENERAL__MFA_REQUIRED_OVERRIDE,
  GENERAL__MFA_REQUIRED_OVERRIDE_HELPER_TEXT,
  GENERAL__MFA_REQUIRED_OVERRIDE_NONE,
  GENERAL__MFA_REQUIRED_OVERRIDE_NOT_REQUIRED,
  GENERAL__MFA_REQUIRED_OVERRIDE_REQUIRED,
} from 'translations/constants';
import MfaRequiredOverride from 'types/MfaRequiredOverride';
import { UPMfaOverrideDropdownProps } from './types';

const REQUIRED = 'required';
const NOT_REQUIRED = 'notRequired';
const NO_OVERRIDE = 'noOverride';

const toDropdownValue = (value: MfaRequiredOverride): string => {
  if (value === true) return REQUIRED;
  if (value === false) return NOT_REQUIRED;
  return NO_OVERRIDE;
};

const fromDropdownValue = (value: string): MfaRequiredOverride => {
  if (value === REQUIRED) return true;
  if (value === NOT_REQUIRED) return false;
  return null;
};

/**
 * Tri-state control for the per-user "MFA mandatory" override.
 *
 * Copy note for the translation values: MFA is opt-in per user and there is no agency-wide role
 * default behind it, so the empty state must not read as "use agency default" or "inherit from
 * role" - there is nothing to inherit. It is a plain "No override". The setting is also stored but
 * not enforced: no enrollment flow, no TOTP, no challenge at login, so the tooltip must not imply
 * the user will be prompted for anything.
 */
const UPMfaOverrideDropdown = (props: UPMfaOverrideDropdownProps) => {
  const { value, onChange, className, disabled, hideNoOverrideOption } = props;
  const [t] = useTranslation();

  const items: UPDropdownItem[] = useMemo(() => {
    const options = [
      { value: REQUIRED, label: t(GENERAL__MFA_REQUIRED_OVERRIDE_REQUIRED) },
      { value: NOT_REQUIRED, label: t(GENERAL__MFA_REQUIRED_OVERRIDE_NOT_REQUIRED) },
    ];
    if (hideNoOverrideOption) return options;
    return [...options, { value: NO_OVERRIDE, label: t(GENERAL__MFA_REQUIRED_OVERRIDE_NONE) }];
  }, [hideNoOverrideOption, t]);

  return (
    <UPDropdown
      items={items}
      value={toDropdownValue(value)}
      onChange={event => onChange(fromDropdownValue(event.target.value))}
      label={t(GENERAL__MFA_REQUIRED_OVERRIDE)}
      labelTooltip={t(GENERAL__MFA_REQUIRED_OVERRIDE_HELPER_TEXT)}
      className={className}
      disabled={disabled}
    />
  );
};

export default UPMfaOverrideDropdown;
